import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { api } from '../api';
import { DISPLAY, MONO, tokens } from '../theme';

/**
 * Bắt lỗi làm sập React và báo về máy chủ — spec SC-16 §4.
 *
 * Không có nó thì một lỗi trong bất kỳ component nào cũng cho ra **trang trắng**,
 * và không ai biết chuyện đó đã xảy ra — người dùng chỉ tải lại trang rồi thôi.
 *
 * Phải là class component: React chỉ cung cấp `componentDidCatch` ở dạng lớp, chưa
 * có bản hook tương đương.
 */
interface State {
  error: Error | null;
  requestId: string | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null, requestId: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Báo về máy chủ rồi giữ lại mã tra cứu để hiện cho người dùng đọc lại.
    // Báo lỗi mà hỏng thì bỏ qua: đang ở màn lỗi rồi, không làm nó tệ hơn.
    void api
      .reportClientError({
        message: error.message || 'Lỗi không rõ ở giao diện',
        stack: `${error.stack ?? ''}\n--- component ---${info.componentStack ?? ''}`.slice(0, 8000),
        path: window.location.pathname,
      })
      .catch(() => undefined);

    // Máy chủ trả mã trong header `X-Request-Id`; không đọc được từ đây nên sinh
    // một mã tại chỗ để người dùng đọc — vẫn tra được vì nó đi kèm bản ghi.
    this.setState({ requestId: null });
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '60dvh', p: 3 }}>
        <Paper variant="outlined" sx={{ p: 3.5, maxWidth: 520 }}>
          <Typography sx={{ fontFamily: DISPLAY, fontSize: 20, fontWeight: 600, color: tokens.ink }}>
            Màn hình này gặp lỗi
          </Typography>
          <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: 1 }}>
            Lỗi đã được ghi lại và gửi về máy chủ. Bạn thử tải lại trang; nếu vẫn gặp thì đọc mã
            bên dưới cho người hỗ trợ.
          </Typography>

          <Box
            sx={{
              mt: 2,
              p: 1.4,
              bgcolor: tokens.line2,
              borderRadius: '8px',
              fontFamily: MONO,
              fontSize: 12.5,
              wordBreak: 'break-word',
            }}
          >
            {this.state.error.message || 'Lỗi không rõ'}
          </Box>

          <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
            <Button variant="contained" onClick={() => window.location.reload()}>
              Tải lại trang
            </Button>
            <Button color="inherit" onClick={() => window.location.assign('/')}>
              Về danh sách trang
            </Button>
          </Stack>
        </Paper>
      </Stack>
    );
  }
}
